import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { HubGame } from '@/types';
import { StatusChip } from '@/components/ui/StatusChip';
import { displayTitle } from '@/lib/displayTitle';
import { gameMotionPreview, gamePoster, gamePosterFallback } from '@/lib/gameArt';
import { observeTile, spotlightEnabled } from '@/lib/tileSpotlight';
import { fetchMediaBlob } from '@/api/client';

interface GameTileProps {
  game: HubGame;
  accessToken?: string | null;
  sessionId?: string | null;
}

function gameStatus(game: HubGame): string | null {
  if (game.flags?.isInMaintenance) return 'maintenance';
  if (game.flags?.isComingSoon) return 'coming_soon';
  if (game.flags?.isActive === false) return 'coming_soon';
  return null;
}

/**
 * Library tile: static poster by default; motion preview once the tile is
 * active — hover or focus with a pointer, scrolled into the viewport centre on
 * touch. Live → authenticated GIF (blob). Slot → public intro MP4 (muted loop).
 */
export function GameTile({ game, accessToken, sessionId }: GameTileProps) {
  const status = gameStatus(game);
  const genre = game.tags?.[0] ?? game.category;
  const preview = gameMotionPreview(game);

  const [poster, setPoster] = useState(() => gamePoster(game));
  const [gifSrc, setGifSrc] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [spotlit, setSpotlit] = useState(false);
  const gifLoadedRef = useRef(false);
  const gifObjectUrlRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const tileRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    setPoster(gamePoster(game));
    gifLoadedRef.current = false;
    if (gifObjectUrlRef.current) {
      URL.revokeObjectURL(gifObjectUrlRef.current);
      gifObjectUrlRef.current = null;
    }
    setGifSrc(null);
  }, [game]);

  useEffect(() => {
    return () => {
      if (gifObjectUrlRef.current) {
        URL.revokeObjectURL(gifObjectUrlRef.current);
        gifObjectUrlRef.current = null;
      }
    };
  }, []);

  const ensureGifLoaded = useCallback(() => {
    if (preview?.kind !== 'auth-gif' || !accessToken || gifLoadedRef.current || gifSrc) return;
    gifLoadedRef.current = true;

    fetchMediaBlob(preview.path, { accessToken, sessionId })
      .then((blob) => {
        if (gifObjectUrlRef.current) {
          URL.revokeObjectURL(gifObjectUrlRef.current);
        }
        const objectUrl = URL.createObjectURL(blob);
        gifObjectUrlRef.current = objectUrl;
        setGifSrc(objectUrl);
      })
      .catch(() => {
        gifLoadedRef.current = false;
      });
  }, [preview, accessToken, sessionId, gifSrc]);

  const playVideo = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0;
    void el.play().catch(() => undefined);
  }, []);

  const pauseVideo = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.pause();
  }, []);

  const activate = useCallback(() => {
    setActive(true);
    if (preview?.kind === 'auth-gif') ensureGifLoaded();
    if (preview?.kind === 'public-video') playVideo();
  }, [preview, ensureGifLoaded, playVideo]);

  const deactivate = useCallback(() => {
    setActive(false);
    if (preview?.kind === 'public-video') pauseVideo();
  }, [preview, pauseVideo]);

  useEffect(() => {
    const element = tileRef.current;
    if (!element || !preview || !spotlightEnabled()) return;

    return observeTile(element, (isActive) => {
      setSpotlit(isActive);
      if (isActive) activate();
      else deactivate();
    });
  }, [preview, activate, deactivate]);

  const showGif = active && preview?.kind === 'auth-gif' && Boolean(gifSrc);
  const showVideo = active && preview?.kind === 'public-video';

  return (
    <Link
      ref={tileRef}
      to={`/library/${encodeURIComponent(game.id)}`}
      className={`game-tile${spotlit ? ' is-spotlit' : ''}`}
      onMouseEnter={activate}
      onMouseLeave={deactivate}
      onFocus={activate}
      onBlur={deactivate}
    >
      <article className="game-tile__card">
        <div className="game-tile__media">
          <img
            src={poster}
            alt=""
            loading="lazy"
            className="game-tile__thumb"
            onError={() => setPoster(gamePosterFallback(game))}
          />

          {preview?.kind === 'public-video' ? (
            <video
              ref={videoRef}
              className={`game-tile__motion${showVideo ? ' is-active' : ''}`}
              src={preview.url}
              muted
              loop
              playsInline
              preload="none"
              aria-hidden="true"
            />
          ) : null}

          {showGif && gifSrc ? (
            <img src={gifSrc} alt="" className="game-tile__motion is-active" />
          ) : null}

          <div className="game-tile__scrim" aria-hidden="true" />
          <div className="game-tile__shine" aria-hidden="true" />

          {status ? (
            <div className="game-tile__status">
              <StatusChip status={status} />
            </div>
          ) : null}

          <div className="game-tile__caption">
            <h3 className="game-tile__title">{displayTitle(game.title)}</h3>
            <span className="game-tile__genre">{genre}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}
